// src/components/articles/ArticleForm.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IoNewspaperOutline,
  IoPersonOutline,
  IoSchoolOutline,
  IoMailOutline,
  IoCheckmarkCircleOutline,
  IoAddCircleOutline,
  IoCloseCircleOutline,
  IoArrowBackOutline,
  IoTextOutline,
  IoBookOutline,
  IoPricetagOutline,
  IoCloudUploadOutline,
  IoDocumentTextOutline,
} from 'react-icons/io5';

import FormField from '../forms/FormField';
import TextArea from '../forms/TextArea';
import CustomSelect from '../forms/CustomSelect';
import TagInput from '../forms/TagInput';
import CustomCheckbox from '../forms/CustomCheckbox';
import DragDropFileUpload from '../common/DragDropFileUpload';
import Spinner from '../common/Spinner';
import toastUtil from '../../utils/toastUtil';

// Subscription helpers / modals
import { secureLocalStorage } from "../../utils/storageUtil";
import ConfirmationModal from "../common/ConfirmationModal";

// Real APIs
import httpService from '../../services/httpService';
import { API_ENDPOINTS } from '../../config/api';

/* ----------------------------------------------------------------
   CONSTANTS
----------------------------------------------------------------- */
const ARTICLE_TYPES = [
  'Research Article', 'Review', 'Systematic Review', 'Meta-analysis',
  'Short Communication', 'Case Report', 'Clinical Image', 'Brief Report',
  'Protocol', 'Perspective', 'Editorial', 'Letter to the Editor', 'Commentary'
];

const STUDY_TYPES = [
  'Clinical Trial – Randomized', 'Clinical Trial – Nonrandomized',
  'Observational – Cohort', 'Observational – Case-Control', 'Observational – Cross-Sectional',
  'Diagnostic Accuracy', 'Prognostic / Risk Prediction', 'Systematic Review', 'Meta-analysis',
  'Qualitative Study', 'Health Economics / Cost-Effectiveness', 'Implementation Science',
  'Animal Study', 'In Vitro / Bench', 'Computational / Simulation',
  'Methodology / Statistical', 'Case Report / Case Series', 'Other'
];

const LANGUAGES = ['US English', 'UK English'];
const CONTENT_LICENSES = ['CC BY 4.0', 'CC BY-NC 4.0', 'All Rights Reserved'];

/**
 * FIELD COUNT (now +5): 31
 * A) Article Info -> journalId, title, runningTitle, abstract,
 *    background, methods, results, conclusions, introduction,
 *    keywords, highlights, references[], menuScript, supplementaryFile, coverLetter
 * B) Authors & Correspondence -> authors[], correspondence {name,email,address}
 * C) Ethics/Funding/Compliance -> ethics{studyType, irbCommittee?, irbApprovalId?}, funding, competingInterests
 * D) Review -> needReviewer, reviewerCount
 * E) Admin & Publishing -> articleType, discipline, language, licenseType, accessType, issueVolume, issueYear, doi
 */

const SectionCard = ({ title, icon, action, children, description }) => (
  <section className="rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
    <div className="flex flex-col gap-2 border-b border-slate-200 px-5 py-4 dark:border-slate-800 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-700 ring-1 ring-blue-100 dark:bg-blue-950/40 dark:text-blue-200 dark:ring-blue-900/40">
            {icon}
          </span>
          <h2 className="truncate text-base font-semibold text-slate-900 dark:text-white">
            {title}
          </h2>
        </div>
        {description ? (
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
            {description}
          </p>
        ) : null}
      </div>

      {action ? <div className="shrink-0">{action}</div> : null}
    </div>

    <div className="p-5">{children}</div>
  </section>
);


const ArticleForm = ({
  mode = 'add', // 'add' or 'edit'
  initialData = null,
  submitHandler,
  backLink = '/articles',
  backText = 'Articles',
  pageTitle = 'Submit New Article',
  pageDescription = 'Complete the form below to submit your article for review',
  buttonText = 'Submit Article',
  loadingText = 'Submitting...'
}) => {
  const navigate = useNavigate();
  const storedUser = secureLocalStorage.getItem('userData', true);

  const [loading, setLoading] = useState(false);

  // -------------------- Journals (API) --------------------
  const [journals, setJournals] = useState([]);
  const [loadingJournals, setLoadingJournals] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoadingJournals(true);
        const res = await httpService.get(API_ENDPOINTS.JOURNALS.LIST);
        const list = res?.data?.success ? (res?.data?.data?.journals || []) : [];
        if (mounted) setJournals(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error('Failed to fetch journals', e);
        toastUtil.error('Failed to load journals');
      } finally {
        mounted && setLoadingJournals(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // -------------------- (A) Article Information --------------------
  const [formData, setFormData] = useState({
    journalId:     initialData?.journalId     || '',
    title:         initialData?.title         || '',
    runningTitle:  initialData?.runningTitle  || '',
    abstract:      initialData?.abstract      || '',
    // NEW: structured abstract parts (all required)
    background:    initialData?.background    || '',
    methods:       initialData?.methods       || '',
    results:       initialData?.results       || '',
    conclusions:   initialData?.conclusions   || '',
    introduction:  initialData?.introduction  || '',
  });

  const [keywords, setKeywords] = useState(initialData?.keywords || []);
  const [keywordInput, setKeywordInput] = useState('');
  const [highlights, setHighlights] = useState(initialData?.highlights || []);
  const [highlightsInput, setHighlightsInput] = useState('');

  // References as ARRAY<string>
  const normalizeInitialReferences = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw.filter((x) => typeof x === 'string');
    if (typeof raw === 'string') {
      return raw.split('\n').map(s => s.trim()).filter(Boolean);
    }
    return [];
  };
  const [references, setReferences] = useState(normalizeInitialReferences(initialData?.references));

  // Files
  const [files, setFiles] = useState({
    menuScript: null,
    supplementaryFile: null,
    coverLetter: null
  });
  const [existingFiles, setExistingFiles] = useState({
    menuScript: initialData?.menuScript || null,
    supplementaryFile: initialData?.supplementaryFile || null,
    coverLetter: initialData?.coverLetter || null,
  });

  useEffect(() => {
    if (mode === 'edit' && initialData) {
      setExistingFiles({
        menuScript: initialData.menuScript || null,
        supplementaryFile: initialData.supplementaryFile || null,
        coverLetter: initialData.coverLetter || null,
      });
      // ensure structured fields are reflected when initialData updates
      setFormData(prev => ({
        ...prev,
        background:   initialData.background   || '',
        methods:      initialData.methods      || '',
        results:      initialData.results      || '',
        conclusions:  initialData.conclusions  || '',
        introduction: initialData.introduction || '',
      }));
      // Prefill accessType on edit
      setAccessType(initialData.accessType || 'free');
    }
  }, [mode, initialData]);

  // -------------------- (B) Authors & Correspondence --------------------
  const [authors, setAuthors] = useState(
    initialData?.authors?.length
      ? initialData.authors
      : [{
        firstName: '',
        lastName: '',
        email: '',
        affiliation: '',
        correspondingAuthor: false,
        firstAuthor: true,   // default first row = first author
        otherAuthor: false
      }]
  );

  const [correspondence, setCorrespondence] = useState(
    initialData?.correspondence || { name: '', email: '', address: '' }
  );

  // -------------------- (C) Ethics, Funding & Compliance --------------------
  const [ethics, setEthics] = useState(
    initialData?.ethics || { studyType: '', irbCommittee: '', irbApprovalId: '' }
  );
  const [funding, setFunding] = useState(initialData?.funding || '');
  const [competingInterests, setCompetingInterests] = useState(initialData?.competingInterests || '');

  // -------------------- (D) Review (26th field) --------------------
  const [needReviewer, setNeedReviewer] = useState(
    initialData?.needReviewer ?? false
  );
  const [reviewerCount, setReviewerCount] = useState(
    initialData?.reviewerCount ?? ''
  );
  const [maxReviewerCount, setMaxReviewerCount] = useState(3); // sensible default

  useEffect(() => {
    (async () => {
      try {
        const res = await httpService.get(API_ENDPOINTS.REVIEWER.COUNT);
        const apiMax = res?.data?.data;
        if (typeof apiMax === 'number' && apiMax > 0) {
          setMaxReviewerCount(apiMax);
        }
      } catch (e) {
        console.warn('Using default reviewer max (3)', e);
      }
    })();
  }, []);

  // -------------------- (E) Administrative & Publishing --------------------
  const [articleType, setArticleType] = useState(initialData?.articleType || '');
  const [discipline, setDiscipline] = useState(initialData?.discipline || '');
  const [language, setLanguage] = useState(initialData?.language || '');
  const [licenseType, setLicenseType] = useState(initialData?.licenseType || '');

  // NEW: Access Type (free | paid)
  const [accessType, setAccessType] = useState(initialData?.accessType || 'free');

  const [issueVolume, setIssueVolume] = useState(initialData?.issueVolume || '');
  const [issueYear,   setIssueYear]   = useState(initialData?.issueYear   || '');
  const [doi,         setDoi]         = useState(initialData?.doi         || '');

  // -------------------- Modals (Subscription & Warning) --------------------
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false });
  const [warningModal, setWarningModal] = useState({ isOpen: false });

  const handlePaymentMessage = () => {
    setDeleteLoading(true);
    navigate('/plans');
  };

  const closeModals = () => {
    setWarningModal({ isOpen: false });
    setDeleteModal({ isOpen: false });
    setDeleteLoading(false);
  };

  // -------------------- Handlers --------------------
  const [errors, setErrors] = useState({});

  const handleBasicChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleFileChange = (e) => {
    const { name, files: uploadedFiles } = e.target;
    if (uploadedFiles.length > 0) {
      const file = uploadedFiles[0];
      setFiles(prev => ({ ...prev, [name]: file }));
      if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Authors
  const handleAuthorChange = (index, field, value) => {
    const updated = [...authors];
    updated[index] = { ...updated[index], [field]: value };
    setAuthors(updated);
    if (errors[`authors.${index}.${field}`]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[`authors.${index}.${field}`];
        return next;
      });
    }
  };

  // EXACT flow: single First Author and single Corresponding Author
  const toggleAuthorCheckbox = (index, field) => {
    const updated = [...authors];
    if (field === 'firstAuthor' && !updated[index].firstAuthor) {
      updated.forEach((a, i) => { updated[i] = { ...updated[i], firstAuthor: i === index }; });
    } else if (field === 'correspondingAuthor' && !updated[index].correspondingAuthor) {
      updated.forEach((a, i) => { updated[i] = { ...updated[i], correspondingAuthor: i === index }; });
    } else {
      updated[index] = { ...updated[index], [field]: !updated[index][field] };
      if (field === 'firstAuthor' && !updated.some(a => a.firstAuthor)) {
        updated[index].firstAuthor = true;
      }
      if (field === 'correspondingAuthor' && !updated.some(a => a.correspondingAuthor)) {
        updated[index].correspondingAuthor = true;
      }
    }
    setAuthors(updated);
  };

  const addAuthor = () => {
    setAuthors(prev => ([
      ...prev,
      {
        firstName: '',
        lastName: '',
        email: '',
        affiliation: '',
        correspondingAuthor: false,
        firstAuthor: false,
        otherAuthor: false
      }
    ]));
  };

  const removeAuthor = (index) => {
    if (authors.length <= 1) return;
    const removingWasFirst = authors[index].firstAuthor;
    const removingWasCorr = authors[index].correspondingAuthor;
    const updated = authors.filter((_, i) => i !== index);
    if (removingWasFirst && updated.length > 0 && !updated.some(a => a.firstAuthor)) {
      updated[0].firstAuthor = true;
    }
    if (removingWasCorr && updated.length > 0 && !updated.some(a => a.correspondingAuthor)) {
      updated[0].correspondingAuthor = true;
    }
    setAuthors(updated);
  };

  // References array handlers
  const addReference = () => setReferences(prev => ([...prev, '' ]));
  const removeReference = (index) => setReferences(prev => prev.filter((_, i) => i !== index));
  const handleReferenceChange = (index, value) => {
    const next = [...references];
    next[index] = value;
    setReferences(next);
  };

  // -------------------- Validation + Focus --------------------
  const focusFirstError = (errs) => {
    const firstKey = Object.keys(errs)[0];
    if (!firstKey) return;
    const selectorSafe = firstKey.replace(/\./g, '\\.');
    const el =
      document.querySelector(`[name="${selectorSafe}"]`) ||
      document.querySelector(`[data-name="${selectorSafe}"]`);
    if (el && typeof el.focus === 'function') {
      el.focus();
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // A) Article Information
    if (!formData.journalId) newErrors.journalId = 'Please select a journal';
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.abstract.trim()) newErrors.abstract = 'Abstract is required';

    // NEW: structured abstract parts (all required)
    if (!formData.background.trim())   newErrors.background   = 'Background is required';
    if (!formData.methods.trim())      newErrors.methods      = 'Methods are required';
    if (!formData.results.trim())      newErrors.results      = 'Results are required';
    if (!formData.conclusions.trim())  newErrors.conclusions  = 'Conclusions are required';
    if (!formData.introduction.trim()) newErrors.introduction = 'Introduction is required';

    if (keywords.length === 0) newErrors.keywords = 'Add at least one keyword';
    if (keywords.length > 8) newErrors.keywords = 'Max 8 keywords';

    if (!files.menuScript && (mode === 'add' || !existingFiles.menuScript)) {
      newErrors.menuScript = 'Manuscript PDF is required';
    }

    // Optional references: if provided, ensure no empty entries
    const trimmedRefs = references.map(r => (r || '').trim()).filter(Boolean);
    if (references.length > 0 && trimmedRefs.length !== references.length) {
      newErrors.references = 'Remove empty reference rows or fill them in';
    }

    // B) Authors & Correspondence
    if (!authors || authors.length === 0) newErrors.authors = 'At least one author is required';
    authors.forEach((a, i) => {
      if (!a.firstName?.trim()) newErrors[`authors.${i}.firstName`] = 'First name required';
      if (!a.lastName?.trim()) newErrors[`authors.${i}.lastName`] = 'Last name required';
      if (!a.email?.trim() || !/\S+@\S+\.\S+/.test(a.email)) newErrors[`authors.${i}.email`] = 'Valid email required';
      if (!a.affiliation?.trim()) newErrors[`authors.${i}.affiliation`] = 'Affiliation required';
    });
    if (!authors.some(a => a.firstAuthor)) newErrors['authors.firstAuthor'] = 'Select a First Author';
    if (!authors.some(a => a.correspondingAuthor)) newErrors['authors.correspondingAuthor'] = 'Select a Corresponding Author';

    if (!correspondence.name?.trim()) newErrors.correspondenceName = 'Correspondence name is required';
    if (!correspondence.email?.trim() || !/\S+@\S+\.\S+/.test(correspondence.email)) newErrors.correspondenceEmail = 'Valid correspondence email required';
    if (!correspondence.address?.trim()) newErrors.correspondenceAddress = 'Correspondence address is required';

    // C) Ethics, Funding & Compliance
    if (!ethics.studyType) newErrors.studyType = 'Study Type is required';
    if (!funding.trim()) newErrors.funding = 'Funding statement is required';
    if (!competingInterests.trim()) newErrors.competingInterests = 'Competing interests are required';

    // D) Review
    if (needReviewer) {
      if (!reviewerCount || Number(reviewerCount) <= 0) {
        newErrors.reviewerCount = 'Please enter a valid reviewer count.';
      } else if (Number(reviewerCount) > maxReviewerCount) {
        newErrors.reviewerCount = `You cannot assign more than ${maxReviewerCount} reviewers.`;
      }
    }

    // E) Administrative & Publishing
    if (!articleType) newErrors.articleType = 'Article Type is required';
    if (!discipline) newErrors.discipline = 'Discipline / Specialty is required';
    if (!language) newErrors.language = 'Language is required';
    if (!licenseType) newErrors.licenseType = 'License Type is required';

    if (issueYear && !/^\d{4}$/.test(issueYear)) newErrors.issueYear = 'Enter a 4-digit year';

    // If you want to enforce accessType selection, uncomment next line:
    // if (!accessType) newErrors.accessType = 'Access type is required';

    setErrors(newErrors);
    return newErrors;
  };

  // -------------------- Submit --------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validateForm();
    if (Object.keys(errs).length) {
      toastUtil.error('Please correct the errors in the form');
      focusFirstError(errs);
      return;
    }

    setLoading(true);
    try {
      const data = new FormData();

      // A) Article Information
      data.append('journalId', formData.journalId);
      data.append('title', formData.title);
      data.append('runningTitle', formData.runningTitle || '');
      data.append('abstract', formData.abstract);

      // NEW: structured abstract parts
      data.append('background',   formData.background);
      data.append('methods',      formData.methods);
      data.append('results',      formData.results);
      data.append('conclusions',  formData.conclusions);
      data.append('introduction', formData.introduction);

      data.append('keywords', JSON.stringify(keywords || []));
      data.append('highlights', JSON.stringify(highlights || []));
      data.append('references', JSON.stringify(references || []));

      if (files.menuScript)        data.append('menuScript', files.menuScript, files.menuScript.name);
      if (files.supplementaryFile) data.append('supplementaryFile', files.supplementaryFile, files.supplementaryFile.name);
      if (files.coverLetter)       data.append('coverLetter', files.coverLetter, files.coverLetter.name);

      // B) Authors & Correspondence
      data.append('authors', JSON.stringify(authors));
      data.append('correspondence', JSON.stringify(correspondence));

      // C) Ethics, Funding & Compliance
      data.append('ethics', JSON.stringify({
        studyType: ethics.studyType,
        irbCommittee: ethics.irbCommittee,
        irbApprovalId: ethics.irbApprovalId
      }));
      data.append('funding', funding);
      data.append('competingInterests', competingInterests);

      // D) Review (26th)
      data.append('needReviewer', needReviewer);
      if (needReviewer) data.append('reviewerCount', reviewerCount);

      // E) Administrative & Publishing
      data.append('articleType', articleType);
      data.append('discipline', discipline);
      data.append('language', language);
      data.append('licenseType', licenseType);

      // NEW: Access type (free | paid)
      data.append('accessType', accessType);

      data.append('issueVolume', issueVolume || '');
      data.append('issueYear', issueYear || '');
      data.append('doi', doi || '');

      await submitHandler(data);
    } catch (error) {
      console.error(`Error ${mode === 'add' ? 'submitting' : 'updating'} article:`, error);
      toastUtil.error(error?.response?.data?.message || `An error occurred while ${mode === 'add' ? 'submitting' : 'updating'} the article`);
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => navigate(backLink);


  // -------------------- RENDER --------------------
  return (
    <>
      {/* Page shell */}
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
        <div className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-6">
          {/* Header */}
          <div className="mb-6">
            {mode === 'edit' && (
              <div className="mb-4">
                <button
                  type="button"
                  onClick={handleGoBack}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800/60"
                >
                  <IoArrowBackOutline className="text-base" />
                  Back to {backText}
                </button>
              </div>
            )}

            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-6">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <h1 className="text-xl font-semibold text-slate-900 dark:text-white sm:text-2xl">
                    {pageTitle}
                  </h1>
                  <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                    {pageDescription}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 ring-1 ring-blue-100 dark:bg-blue-950/40 dark:text-blue-200 dark:ring-blue-900/40">
                    <IoNewspaperOutline />
                    AJMM Submission
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* A) ARTICLE INFORMATION */}
            <SectionCard
              title="Article Information"
              icon={<IoNewspaperOutline className="text-lg" />}
              description="Provide the core details of your manuscript and structured abstract sections."
            >
              <div className="grid grid-cols-1 gap-5">
                {/* Select Journal (REQUIRED) */}
                {loadingJournals ? (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/40">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900 dark:text-white">
                          Select Journal<span className="text-red-500"> *</span>
                        </p>
                        <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">
                          Fetching available journals…
                        </p>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                        <Spinner size="small" />
                        <span className="whitespace-nowrap">Loading</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <CustomSelect
                    label="Select Journal"
                    name="journalId"
                    value={formData.journalId}
                    onChange={handleBasicChange}
                    options={journals.map(j => ({ value: j._id, label: j.title }))}
                    placeholder="Select a journal"
                    error={errors.journalId}
                    required
                    icon={<IoNewspaperOutline />}
                  />
                )}

                <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                  <FormField
                    label="Article Title"
                    name="title"
                    value={formData.title}
                    onChange={handleBasicChange}
                    placeholder="Enter the complete title of your article"
                    error={errors.title}
                    required
                    icon={<IoTextOutline />}
                  />

                  <FormField
                    label="Running Title"
                    name="runningTitle"
                    value={formData.runningTitle}
                    onChange={handleBasicChange}
                    placeholder="Short title used in headers (≤ 60 chars)"
                    icon={<IoTextOutline />}
                  />
                </div>

                <TextArea
                  label="Abstract"
                  name="abstract"
                  value={formData.abstract}
                  onChange={handleBasicChange}
                  placeholder="Provide a concise summary of your article"
                  rows={6}
                  error={errors.abstract}
                  required
                  icon={<IoBookOutline />}
                />

                {/* Structured Abstract */}
                <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                  <TextArea
                    label="Background"
                    name="background"
                    value={formData.background}
                    onChange={handleBasicChange}
                    placeholder="What is already known / why this study?"
                    rows={3}
                    error={errors.background}
                    required
                  />
                  <TextArea
                    label="Methods"
                    name="methods"
                    value={formData.methods}
                    onChange={handleBasicChange}
                    placeholder="Study design, participants, interventions, measurements"
                    rows={3}
                    error={errors.methods}
                    required
                  />
                  <TextArea
                    label="Results"
                    name="results"
                    value={formData.results}
                    onChange={handleBasicChange}
                    placeholder="Key findings with essential statistics"
                    rows={3}
                    error={errors.results}
                    required
                  />
                  <TextArea
                    label="Conclusions"
                    name="conclusions"
                    value={formData.conclusions}
                    onChange={handleBasicChange}
                    placeholder="Main takeaway and implications"
                    rows={3}
                    error={errors.conclusions}
                    required
                  />
                </div>

                <TextArea
                  label="Introduction"
                  name="introduction"
                  value={formData.introduction}
                  onChange={handleBasicChange}
                  placeholder="Context and objective (brief)"
                  rows={3}
                  error={errors.introduction}
                  required
                />

                <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                  <TagInput
                    label="Keywords"
                    tags={keywords}
                    setTags={setKeywords}
                    tagInputValue={keywordInput}
                    setTagInputValue={setKeywordInput}
                    placeholder="Type and press Enter to add keywords..."
                    error={errors.keywords}
                    required
                    maxTags={8}
                    helpText="Add 3–8 keywords. Press Enter after each."
                    icon={<IoPricetagOutline />}
                  />

                  <TagInput
                    label="Highlights / Key Points"
                    tags={highlights}
                    setTags={setHighlights}
                    tagInputValue={highlightsInput}
                    setTagInputValue={setHighlightsInput}
                    placeholder="3–5 short bullets"
                    icon={<IoDocumentTextOutline />}
                  />
                </div>

                {/* References */}
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/40">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-2">
                      <IoDocumentTextOutline className="text-slate-700 dark:text-slate-200" />
                      <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                        References
                      </h3>
                    </div>

                    <button
                      type="button"
                      onClick={addReference}
                      className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/40 disabled:opacity-60"
                    >
                      <IoAddCircleOutline className="text-base" />
                      Add Reference
                    </button>
                  </div>

                  {references.length === 0 && (
                    <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">
                      No references added yet. Click “Add Reference”.
                    </p>
                  )}

                  <div className="mt-4 space-y-3">
                    {references.map((ref, index) => (
                      <div
                        key={index}
                        className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900"
                      >
                        <div className="mb-3 flex items-center justify-between gap-3">
                          <h4 className="text-sm font-semibold text-slate-900 dark:text-white">
                            Reference {index + 1}
                          </h4>
                          <button
                            type="button"
                            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800/60"
                            onClick={() => removeReference(index)}
                          >
                            <IoCloseCircleOutline className="text-base" />
                            Remove
                          </button>
                        </div>

                        <FormField
                          label="Citation"
                          name={`reference-${index}`}
                          value={ref}
                          onChange={(e) => handleReferenceChange(index, e.target.value)}
                          placeholder="e.g., Doe J., Smith A. Title. Journal. Year;Volume(Issue):Pages. doi:..."
                          error={errors?.references}
                        />
                      </div>
                    ))}
                  </div>

                  {errors?.references && (
                    <div className="mt-3 text-sm font-medium text-red-600">
                      {errors.references}
                    </div>
                  )}
                </div>

                {/* Uploads */}
                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                  <div className="mb-4 flex items-center gap-2">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-700 ring-1 ring-blue-100 dark:bg-blue-950/40 dark:text-blue-200 dark:ring-blue-900/40">
                      <IoCloudUploadOutline className="text-lg" />
                    </span>
                    <div className="min-w-0">
                      <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                        Upload Files
                      </h3>
                      <p className="text-xs text-slate-600 dark:text-slate-300">
                        Add your manuscript and any supporting documents.
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                    <DragDropFileUpload
                      title="Manuscript (PDF)"
                      name="menuScript"
                      acceptedFormats=".pdf"
                      value={files.menuScript}
                      onChange={handleFileChange}
                      error={errors.menuScript}
                      required={mode === 'add' || !existingFiles.menuScript}
                      existingFile={mode === 'edit' ? existingFiles.menuScript : null}
                      maxFileSize={20}
                    />
                    <DragDropFileUpload
                      title="Supplementary Files"
                      name="supplementaryFile"
                      acceptedFormats=".pdf,.doc,.docx,.xls,.xlsx"
                      value={files.supplementaryFile}
                      onChange={handleFileChange}
                      existingFile={mode === 'edit' ? existingFiles.supplementaryFile : null}
                      maxFileSize={50}
                    />
                    <DragDropFileUpload
                      title="Cover Image / Graphical Abstract (.png)"
                      name="coverLetter"
                      acceptedFormats=".png"
                      value={files.coverLetter}
                      onChange={handleFileChange}
                      existingFile={mode === 'edit' ? existingFiles.coverLetter : null}
                      maxFileSize={10}
                    />
                  </div>
                </div>
              </div>
            </SectionCard>

            {/* B) AUTHORS & CORRESPONDENCE */}
            <SectionCard
              title="Authors"
              icon={<IoPersonOutline className="text-lg" />}
              action={
                <button
                  type="button"
                  onClick={addAuthor}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                >
                  <IoAddCircleOutline className="text-base" />
                  Add Author
                </button>
              }
              description="Add all authors and select one First Author and one Corresponding Author."
            >
              <div className="space-y-4">
                {authors.map((author, index) => (
                  <div
                    key={index}
                    className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900"
                  >
                    <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                        Author {index + 1}
                      </h3>

                      {index > 0 && (
                        <button
                          type="button"
                          onClick={() => removeAuthor(index)}
                          className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800/60"
                        >
                          <IoCloseCircleOutline className="text-base" />
                          Remove
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                      <FormField
                        label="First Name"
                        name={`authors.${index}.firstName`}
                        value={author.firstName || ''}
                        onChange={(e) => handleAuthorChange(index, 'firstName', e.target.value)}
                        placeholder="First Name"
                        error={errors[`authors.${index}.firstName`]}
                        required
                        icon={<IoPersonOutline />}
                      />
                      <FormField
                        label="Last Name"
                        name={`authors.${index}.lastName`}
                        value={author.lastName || ''}
                        onChange={(e) => handleAuthorChange(index, 'lastName', e.target.value)}
                        placeholder="Last Name"
                        error={errors[`authors.${index}.lastName`]}
                        required
                        icon={<IoPersonOutline />}
                      />
                      <FormField
                        label="Email"
                        name={`authors.${index}.email`}
                        type="email"
                        value={author.email || ''}
                        onChange={(e) => handleAuthorChange(index, 'email', e.target.value)}
                        placeholder="Email Address"
                        error={errors[`authors.${index}.email`]}
                        required
                        icon={<IoMailOutline />}
                      />
                      <FormField
                        label="Affiliation"
                        name={`authors.${index}.affiliation`}
                        value={author.affiliation || ''}
                        onChange={(e) => handleAuthorChange(index, 'affiliation', e.target.value)}
                        placeholder="University or Institution"
                        error={errors[`authors.${index}.affiliation`]}
                        required
                        icon={<IoSchoolOutline />}
                      />
                    </div>

                    <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/40">
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                        <div className="flex items-center gap-2">
                          <CustomCheckbox
                            id={`firstAuthor-${index}`}
                            checked={!!author.firstAuthor}
                            onChange={() => toggleAuthorCheckbox(index, 'firstAuthor')}
                            label="First Author"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <CustomCheckbox
                            id={`correspondingAuthor-${index}`}
                            checked={!!author.correspondingAuthor}
                            onChange={() => toggleAuthorCheckbox(index, 'correspondingAuthor')}
                            label="Corresponding Author"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <CustomCheckbox
                            id={`otherAuthor-${index}`}
                            checked={!!author.otherAuthor}
                            onChange={() => toggleAuthorCheckbox(index, 'otherAuthor')}
                            label="Other Author"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                  <div className="mb-4 flex items-center gap-2">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-700 ring-1 ring-blue-100 dark:bg-blue-950/40 dark:text-blue-200 dark:ring-blue-900/40">
                      <IoMailOutline className="text-lg" />
                    </span>
                    <div className="min-w-0">
                      <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                        Correspondence Details
                      </h3>
                      <p className="text-xs text-slate-600 dark:text-slate-300">
                        The corresponding author’s official contact information.
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                    <FormField
                      label="Correspondence Name"
                      name="correspondenceName"
                      value={correspondence.name}
                      onChange={(e) => setCorrespondence(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Full name"
                      error={errors.correspondenceName}
                      required
                      icon={<IoPersonOutline />}
                    />
                    <FormField
                      label="Correspondence Email"
                      name="correspondenceEmail"
                      value={correspondence.email}
                      onChange={(e) => setCorrespondence(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="name@example.org"
                      error={errors.correspondenceEmail}
                      required
                      icon={<IoMailOutline />}
                    />
                  </div>

                  <div className="mt-5">
                    <TextArea
                      label="Correspondence Address"
                      name="correspondenceAddress"
                      value={correspondence.address}
                      onChange={(e) => setCorrespondence(prev => ({ ...prev, address: e.target.value }))}
                      placeholder="Department, Institution, City, Country"
                      rows={3}
                      error={errors.correspondenceAddress}
                      required
                      icon={<IoSchoolOutline />}
                    />
                  </div>
                </div>
              </div>
            </SectionCard>

            {/* C) ETHICS, FUNDING & COMPLIANCE */}
            <SectionCard
              title="Ethics, Funding & Compliance"
              icon={<IoCheckmarkCircleOutline className="text-lg" />}
              description="Provide study ethics information and all disclosures."
            >
              <div className="grid grid-cols-1 gap-5">
                <CustomSelect
                  label="Study Type"
                  name="studyType"
                  value={ethics.studyType}
                  onChange={(e) => setEthics(prev => ({ ...prev, studyType: e.target.value }))}
                  options={STUDY_TYPES.map(s => ({ value: s, label: s }))}
                  error={errors.studyType}
                  required
                  icon={<IoDocumentTextOutline />}
                />

                <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                  <FormField
                    label="IRB / Ethics Committee"
                    name="irbCommittee"
                    value={ethics.irbCommittee}
                    onChange={(e) => setEthics(prev => ({ ...prev, irbCommittee: e.target.value }))}
                    placeholder="Committee name"
                    error={errors.irbCommittee}
                  />
                  <FormField
                    label="IRB Approval ID"
                    name="irbApprovalId"
                    value={ethics.irbApprovalId}
                    onChange={(e) => setEthics(prev => ({ ...prev, irbApprovalId: e.target.value }))}
                    placeholder="Approval number"
                    error={errors.irbApprovalId}
                  />
                </div>

                <TextArea
                  label="Funding Statement"
                  name="funding"
                  value={funding}
                  onChange={(e) => setFunding(e.target.value)}
                  rows={3}
                  error={errors.funding}
                  required
                />

                <TextArea
                  label="Competing Interests"
                  name="competingInterests"
                  value={competingInterests}
                  onChange={(e) => setCompetingInterests(e.target.value)}
                  rows={2}
                  error={errors.competingInterests}
                  required
                />
              </div>
            </SectionCard>

            {/* D) REVIEW INFORMATION */}
            <SectionCard
              title="Review Information"
              icon={<IoCheckmarkCircleOutline className="text-lg" />}
              description="Choose whether you need reviewers and specify reviewer count."
            >
              <div className="grid grid-cols-1 gap-5">
                <CustomSelect
                  label="Need Reviewer?"
                  name="needReviewer"
                  value={needReviewer ? 'true' : 'false'}
                  onChange={(e) => {
                    const val = e.target.value === 'true';

                    // If trying to enable reviewers, check subscription first
                    if (val && !storedUser?.isSubmitterPaid) {
                      setNeedReviewer(false);
                      setDeleteModal({ isOpen: true });
                      return;
                    }

                    // If disabling reviewers, show warning and clear errors/count
                    if (!val) {
                      setWarningModal({ isOpen: true });
                      setErrors(prev => {
                        const next = { ...prev };
                        delete next.reviewerCount;
                        return next;
                      });
                      setReviewerCount('');
                    }

                    setNeedReviewer(val);
                  }}
                  options={[
                    { value: 'true', label: 'Yes' },
                    { value: 'false', label: 'No' }
                  ]}
                  required
                  icon={<IoCheckmarkCircleOutline />}
                />

                {needReviewer && (
                  <FormField
                    label="Number of Reviewers"
                    name="reviewerCount"
                    type="number"
                    value={reviewerCount}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === '') {
                        setReviewerCount('');
                        setErrors(prev => ({ ...prev, reviewerCount: 'Please enter a valid reviewer count.' }));
                        return;
                      }
                      const num = Number(raw);
                      let msg = '';
                      if (!Number.isFinite(num) || num <= 0) {
                        msg = 'Reviewer count must be at least 1.';
                      } else if (num > maxReviewerCount) {
                        msg = `You cannot assign more than ${maxReviewerCount} reviewers.`;
                      }
                      setReviewerCount(num);
                      setErrors(prev => ({ ...prev, reviewerCount: msg }));
                    }}
                    error={errors.reviewerCount}
                    placeholder="Enter reviewer count"
                    helpText={`Maximum allowed: ${maxReviewerCount}`}
                    required
                    icon={<IoPersonOutline />}
                  />
                )}
              </div>
            </SectionCard>

            {/* E) ADMINISTRATIVE & PUBLISHING */}
            <SectionCard
              title="Administrative & Publishing"
              icon={<IoDocumentTextOutline className="text-lg" />}
              description="Set article metadata, license, access type and publishing details."
            >
              <div className="grid grid-cols-1 gap-5">
                <CustomSelect
                  label="Article Type"
                  name="articleType"
                  value={articleType}
                  onChange={(e) => setArticleType(e.target.value)}
                  options={ARTICLE_TYPES.map(t => ({ value: t, label: t }))}
                  error={errors.articleType}
                  required
                  icon={<IoDocumentTextOutline />}
                />

                <CustomSelect
                  label="Discipline / Specialty"
                  name="discipline"
                  value={discipline}
                  onChange={(e) => setDiscipline(e.target.value)}
                  options={[
                    { value: '', label: 'Select a specialty' },
                    { value: 'Cardiology', label: 'Cardiology' },
                    { value: 'Oncology', label: 'Oncology' },
                    { value: 'Neurology', label: 'Neurology' },
                    { value: 'Endocrinology', label: 'Endocrinology' },
                    { value: 'Pulmonology', label: 'Pulmonology' },
                    { value: 'Nephrology', label: 'Nephrology' },
                    { value: 'Gastroenterology', label: 'Gastroenterology' },
                    { value: 'Rheumatology', label: 'Rheumatology' },
                    { value: 'Infectious Diseases', label: 'Infectious Diseases' },
                    { value: 'Dermatology', label: 'Dermatology' },
                    { value: 'Obstetrics & Gynecology', label: 'Obstetrics & Gynecology' },
                    { value: 'Pediatrics', label: 'Pediatrics' },
                    { value: 'Geriatrics', label: 'Geriatrics' },
                    { value: 'Emergency Medicine', label: 'Emergency Medicine' },
                    { value: 'Radiology', label: 'Radiology' },
                    { value: 'Pathology', label: 'Pathology' },
                    { value: 'Public Health', label: 'Public Health' },
                    { value: 'AI in Medicine', label: 'AI in Medicine' }
                  ]}
                  error={errors.discipline}
                  required
                  icon={<IoPricetagOutline />}
                />

                <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                  <CustomSelect
                    label="Language"
                    name="language"
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                    options={LANGUAGES.map(x => ({ value: x, label: x }))}
                    error={errors.language}
                    required
                  />
                  <CustomSelect
                    label="License Type"
                    name="licenseType"
                    value={licenseType}
                    onChange={(e) => setLicenseType(e.target.value)}
                    options={CONTENT_LICENSES.map(x => ({ value: x, label: x }))}
                    error={errors.licenseType}
                    required
                  />
                </div>

                {/* Access Type */}
                <CustomSelect
                  label="Article Access"
                  name="accessType"
                  value={accessType}
                  onChange={(e) => {
                    setAccessType(e.target.value);
                    if (errors.accessType) setErrors(prev => ({ ...prev, accessType: '' }));
                  }}
                  options={[
                    { value: 'free', label: 'Free' },
                    { value: 'paid', label: 'Paid' }
                  ]}
                  icon={<IoPricetagOutline />}
                  helpText="Choose whether this article is freely accessible or paywalled."
                />

                <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
                  <FormField
                    label="Issue Volume"
                    name="issueVolume"
                    value={issueVolume}
                    onChange={(e) => setIssueVolume(e.target.value)}
                    placeholder="e.g., 2"
                  />
                  <FormField
                    label="Issue Year"
                    name="issueYear"
                    value={issueYear}
                    onChange={(e) => setIssueYear(e.target.value)}
                    placeholder="e.g., 2025"
                    error={errors.issueYear}
                  />
                </div>

                <FormField
                  label="DOI (assigned on acceptance)"
                  name="doi"
                  value={doi}
                  onChange={(e) => setDoi(e.target.value)}
                  placeholder="Auto after acceptance"
                  required
                />
              </div>
            </SectionCard>

            {/* ACTIONS */}
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
              <button
                type="button"
                onClick={handleGoBack}
                disabled={loading}
                className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800/60"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={loading || (journals.length === 0)}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/40 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Spinner size="small" />
                    {loadingText}
                  </>
                ) : (
                  <>
                    <IoCheckmarkCircleOutline className="text-base" />
                    {buttonText}
                  </>
                )}
              </button>
            </div>
          </form>

          {/* MODALS */}
          <ConfirmationModal
            isOpen={deleteModal.isOpen}
            onClose={closeModals}
            onConfirm={handlePaymentMessage}
            confirmVariant="success"
            title="Subscribe Publishing Access Plan"
            message={`Unlock full access to assign reviewers with a Premium submission plan.`}
            confirmText={deleteLoading ? "Subscribe Now..." : "Subscribe Now"}
            cancelText="Cancel"
            type="info"
          />

          <ConfirmationModal
            isOpen={warningModal.isOpen}
            onClose={closeModals}
            onConfirm={closeModals}
            title="Reviewer Requirement Warning"
            message={
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-left text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-100">
                <p className="font-semibold">Warning:</p>
                <p className="mt-1">You are disabling reviewer assignment for this article.</p>
                <p className="mt-2">
                  To maintain peer-review standards, it’s recommended to assign reviewers (usually 3).
                </p>
                <p className="mt-2">Do you still want to proceed without reviewers?</p>
              </div>
            }
            confirmText="Proceed"
            cancelText="Cancel"
            type="warning"
          />
        </div>
      </div>
    </>
  );
};

export default ArticleForm;
